/**
 * Wallet Connecting Modal
 *
 * Presentational component for the wallet connecting flow:
 * 1. "Connecting" - waiting for wallet extension approval
 * 2. "Checking" - wallet connected, checking account status
 * 3. "Connected" - success, auto-closing soon
 * 4. "Not Whitelisted Initial" - access denied, shows wallet icon + cross
 * 5. "Not Whitelisted" - final state with address, waitlist link, browse button
 */

import React from "react";

import { RightShortIcon } from "../assets/icons/arrows/RightShortIcon";
import { CloseIcon as CrossIcon } from "../assets/icons/v2/CloseIcon";
import { AnimatedLogo } from "../components/AnimatedLogo/AnimatedLogo";
import { truncateAddress } from "../utils/format";

export type ConnectingStep =
    | "connecting"
    | "checking"
    | "connected"
    | "not-whitelisted-initial"
    | "not-whitelisted";

interface WalletConnectingModalProps {
    step: ConnectingStep;
    walletName: string;
    walletIcon: string;
    walletAddress?: string;
    onClose: () => void;
    onGoToSite: () => void;
    showGoToSite: boolean;
}

const STEP_CONTENT: Record<
    Exclude<ConnectingStep, "not-whitelisted">,
    { title: string; subtitle: string; footer: (walletName: string) => string }
> = {
    connecting: {
        title: "Connecting Wallet",
        subtitle: "Connecting to Solomon on Solana",
        footer: (walletName) =>
            `Please check your ${walletName} wallet to connect to the app.`
    },
    checking: {
        title: "Checking Wallet",
        subtitle: "Checking account status",
        footer: () => "Please wait while we check your account."
    },
    connected: {
        title: "Wallet Connected",
        subtitle: "Successfully connected to Solomon",
        footer: () => "You will now be taken to the app."
    },
    "not-whitelisted-initial": {
        title: "Wallet Not Whitelisted",
        subtitle: "Access restricted",
        footer: () =>
            "During the initial phases, access to utilize the application will be restricted to only whitelisted users. Once the initial phase passes, access will be opened to all."
    }
};

export function WalletConnectingModal({
    step,
    walletName,
    walletIcon,
    walletAddress,
    onClose,
    onGoToSite,
    showGoToSite
}: WalletConnectingModalProps): React.ReactElement {
    if (step === "not-whitelisted") {
        return (
            <>
                {/* Blur Background */}
                <div
                    className="fixed inset-0 bg-[#111114]/50 transition-opacity duration-300 z-40 backdrop-blur opacity-100"
                    onClick={onClose}
                    aria-hidden="true"
                />
                {/* Modal Content */}
                <div
                    className="panel fixed z-50 w-full inset-0 m-auto max-w-screen-lg h-fit"
                    onClick={(e): void => e.stopPropagation()}
                >
                    <div className="menu rounded-b-md flex flex-col w-full max-h-[630px] overflow-hidden">
                        {/* Header */}
                        <div className="flex flex-col w-full px-md pt-md pb-sm gap-2xs shrink-0">
                            <div className="flex w-full items-center justify-between">
                                <h2 className="text-h2 font-bold text-foreground-base">
                                    Wallet Not Whitelisted
                                </h2>
                                <button
                                    onClick={onClose}
                                    className="text-foreground-base
                                        hover:text-primary-base
                                        active:text-primary-muted
                                        transition-colors duration-200
                                        cursor-pointer"
                                    aria-label="Close"
                                >
                                    <CrossIcon className="w-auto h-lg" />
                                </button>
                            </div>
                            <p className="text-sm text-foreground-muted">
                                Access restricted
                            </p>
                        </div>

                        {/* Center Section - White background with address */}
                        <div className="bg-white flex flex-col items-center justify-center py-2xl gap-md flex-1 min-h-0">
                            <span className="text-lg font-bold text-black">
                                {walletAddress
                                    ? truncateAddress(walletAddress, 8, 8)
                                    : "Unknown"}
                            </span>
                            <p className="text-sm text-black/70 text-center px-md">
                                Your wallet is not whitelisted for access.
                            </p>
                        </div>

                        {/* Middle Section - Waitlist info */}
                        <div className="flex flex-col items-center px-md py-md shrink-0">
                            <p className="text-sm text-foreground-muted text-center">
                                You can get added to the waitlist for access by
                                filling out this form{" "}
                                <a
                                    href="https://solomonlabs.typeform.com/joinwhitelist"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link font-bold"
                                >
                                    here
                                </a>
                                .
                            </p>
                        </div>

                        {/* Footer Banner */}
                        <div className="bg-surface-imprint flex flex-col items-center gap-sm px-md py-md shrink-0">
                            <p className="text-xs text-foreground-muted text-start">
                                During the initial phases, access to utilize the
                                application will be restricted to only
                                whitelisted users. Once the initial phase
                                passes, access will be opened to all.
                            </p>
                        </div>

                        {/* Browse Button */}
                        <div className="px-md py-md shrink-0">
                            <button
                                onClick={onClose}
                                className="btn-primary btn-sm w-full items-center cursor-pointer"
                            >
                                Browse Site Without Access
                                <RightShortIcon className="w-auto h-sm" />
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    const content = STEP_CONTENT[step];

    return (
        <>
            {/* Blur Background */}
            <div
                className="fixed inset-0 bg-[#111114]/50 transition-opacity duration-300 z-40 backdrop-blur opacity-100"
                onClick={onClose}
                aria-hidden="true"
            />
            {/* Modal Content */}
            <div
                className="panel fixed z-50 w-full inset-0 m-auto max-w-screen-lg h-fit"
                onClick={(e): void => e.stopPropagation()}
            >
                <div className="menu rounded-b-md flex flex-col w-full max-h-[630px] overflow-hidden">
                    {/* Header */}
                    <div className="flex flex-col bg-surface-neutral w-full px-md pt-md pb-sm gap-2xs shrink-0">
                        <div className="flex w-full items-center justify-between">
                            <h2 className="text-h2 font-bold text-foreground-base">
                                {content.title}
                            </h2>
                            <button
                                onClick={onClose}
                                className="text-foreground-base
                                    hover:text-primary-base
                                    active:text-primary-muted
                                    transition-colors duration-200
                                    cursor-pointer"
                                aria-label="Close"
                            >
                                <CrossIcon className="w-auto h-lg" />
                            </button>
                        </div>
                        <p className="text-sm text-foreground-muted">
                            {content.subtitle}
                        </p>
                    </div>

                    {/* Center Section - White background with wallet icon */}
                    <div className="text-foreground-base bg-surface-neutral flex flex-col items-center justify-center py-2xl gap-md flex-1 min-h-0">
                        {/* Wallet Icon */}
                        <img
                            src={walletIcon}
                            alt={walletName}
                            className="w-[10rem] h-[10rem]"
                        />
                        <span className="text-sm font-bold">{walletName}</span>
                        {/* Status Icon */}
                        <div className="h-xl flex items-center justify-center">
                            {step === "connected" ? (
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-auto h-lg"
                                    aria-label="Connected"
                                >
                                    <path
                                        d="M3 29.3872L6.85491 24.7931L21.8755 37.3968L18.0206 41.9909L3 29.3872Z"
                                        fill="currentColor"
                                    />
                                    <path
                                        d="M40.4059 6.00928L45 9.86419L18.0533 41.978L13.4592 38.1231L40.4059 6.00928Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            ) : step === "not-whitelisted-initial" ? (
                                <CrossIcon className="w-auto h-lg" />
                            ) : (
                                <AnimatedLogo className="w-auto h-md text-foreground-base" />
                            )}
                        </div>
                    </div>

                    {/* Footer Banner */}
                    <div className="bg-[#ECEBDA] flex flex-col items-center gap-sm px-md py-md shrink-0">
                        <p className="text-xs text-foreground-muted text-center">
                            {content.footer(walletName)}
                        </p>
                        {showGoToSite && (
                            <button
                                onClick={onGoToSite}
                                className="text-xs font-bold text-[#565A56]
                                    hover:text-primary-muted
                                    transition-colors duration-200
                                    cursor-pointer"
                            >
                                Go to Site Manually
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
